import { motion } from "framer-motion";
import { Plus, Tag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { combos } from "@/data/combos";
import { getProductById } from "@/data/menu";
import { localize, formatTRY } from "@/lib/format";

export function Combos() {
  const { addItem } = useCart();
  const { lang, t } = useLang();
  const active = combos.filter((c) => c.available);
  if (!active.length) return null;

  const addCombo = (combo) => {
    addItem({ comboId: combo.id, isCombo: true, quantity: 1, selections: {}, note: "" });
    toast.success(`${localize(combo.name, lang)} — ${t("add")}`);
  };

  return (
    <section data-testid="combos-section">
      <h2 className="mb-3 flex items-center gap-2 px-4 font-heading text-lg font-extrabold">
        <Tag className="h-4 w-4 text-primary" /> {t("combos")}
      </h2>
      <div className="no-scrollbar flex gap-4 overflow-x-auto px-4 pb-1">
        {active.map((combo, i) => {
          const regular = combo.componentIds.reduce((sum, id) => sum + (getProductById(id)?.basePrice || 0), 0);
          const save = regular - combo.price;
          return (
            <motion.div
              key={combo.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.06, 0.3) }}
              className="w-64 shrink-0 overflow-hidden rounded-2xl border border-border bg-card shadow-soft"
              data-testid={`combo-card-${combo.id}`}
            >
              <div className="relative h-28 w-full overflow-hidden bg-muted">
                <img src={combo.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                {save > 0 && (
                  <span className="absolute start-2 top-2 rounded-full bg-success px-2.5 py-1 text-xs font-bold text-white">
                    {formatTRY(save)} {t("savings")}
                  </span>
                )}
              </div>
              <div className="p-3.5">
                <h3 className="font-heading text-[15px] font-bold">{localize(combo.name, lang)}</h3>
                <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{localize(combo.description, lang)}</p>
                <div className="mt-2.5 flex items-center justify-between">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-heading text-base font-extrabold text-primary">{formatTRY(combo.price)}</span>
                    {save > 0 && <span className="text-xs text-muted-foreground line-through">{formatTRY(regular)}</span>}
                  </div>
                  <button
                    onClick={() => addCombo(combo)}
                    className="flex items-center gap-1 rounded-full brand-gradient px-3 py-1.5 text-xs font-bold text-white active:scale-95"
                    data-testid={`combo-add-${combo.id}`}
                  >
                    <Plus className="h-3.5 w-3.5" strokeWidth={2.5} /> {t("makeCombo")}
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
